"use client";

import { useState, useEffect, useRef } from "react";
import { Highlight, themes } from "prism-react-renderer";
import { DecodeText } from "@/components/ui/decode-text";

const cards = [
  {
    num: "01",
    title: "Objects in. Capabilities out.",
    desc: "A paradigm that goes beyond tool calls. Pass objects by reference in, specify a return type and get a runtime object back, types intact.",
    secondDesc:
      "An agent with a persistent working memory that can think outside of the context window.",
    file: "agent.py",
    language: "python" as const,
    code: `from agentica import spawn
from sdk import Database, UserId

agent = await spawn(
    "Answer questions by searching the web.",
    model='openrouter:google/gemini-2.5-flash',
    scope={"database": Database(...)}
)

await agent.call(
    dict[UserId, str],
    "For each user, summarise their spending habits."
)`,
  },
  {
    num: "02",
    title: "If you can import it, they can use it",
    desc: "Your code? SDK clients? APIs? No wrapping, just import and pass them in scope.",
    file: "agent.py",
    language: "python" as const,
    code: `# 3rd party API for fetching market data
from twelvedata import TDClient
from agentica import spawn

# Construct an instance
market_data = TDClient(apikey=...)

# Pass the client to the agent
data_agent = await spawn(
    premise="You are a financial data analyst.",
    model="anthropic/claude-opus-4.5",
    scope={'TDClient': TDClient, 'market_data': market_data}
)`,
  },
  {
    num: "03",
    title: "Agents that delegate capabilities",
    desc: "Agents can pass objects from their own scope to subagents — not just delegate tasks. This is autonomous orchestration.",
    file: "trace.log",
    language: "python" as const,
    code: `[12:34:56] orchestrator → spawn researcher
[12:34:57] researcher   ← initialized
[12:34:58] orchestrator → call research()
[12:35:02] researcher   → tool: web_search
[12:35:05] researcher   ← results: 15 items
[12:35:06] orchestrator ← research complete
[12:35:07] orchestrator → spawn writer
[12:35:08] writer       ← initialized
[12:35:09] orchestrator → call write(research)
[12:35:14] writer       ← draft complete`,
  },
  {
    num: "04",
    title: "Observability built for code execution",
    desc: "Every agent action is observable and traceable. See generated code, execution results, and spawn trees — rendered live, zero config.",
    file: "dashboard",
    image: "/images/platform/observability.png",
  },
];

function CodeSnippet({ code, language }: { code: string; language: string }) {
  return (
    <Highlight theme={themes.nightOwl} code={code} language={language}>
      {({ tokens, getLineProps, getTokenProps }) => (
        <pre className="text-[13px] leading-relaxed whitespace-pre overflow-x-auto">
          {tokens.map((line, i) => (
            <div key={i} {...getLineProps({ line })}>
              {line.map((token, key) => (
                <span key={key} {...getTokenProps({ token })} />
              ))}
            </div>
          ))}
        </pre>
      )}
    </Highlight>
  );
}

function RightPanel({
  card,
  is3d = false,
}: {
  card: (typeof cards)[number];
  is3d?: boolean;
}) {
  return (
    <div
      className="relative rounded-lg border border-white/[0.08] bg-[#0c0c0e] overflow-hidden"
      style={
        is3d
          ? {
              transform: "perspective(900px) rotateY(-2deg) rotateX(1.5deg)",
              transformOrigin: "center center",
              boxShadow:
                "0 0 40px rgba(118, 120, 227, 0.08), 0 0 80px rgba(118, 120, 227, 0.04), 0 20px 60px rgba(0,0,0,0.5)",
            }
          : undefined
      }
    >
      {/* Scanline overlay */}
      {is3d && (
        <div
          className="absolute inset-0 z-10 pointer-events-none"
          style={{
            background:
              "repeating-linear-gradient(to bottom, transparent, transparent 1.5px, rgba(255,255,255,0.015) 1.5px, rgba(255,255,255,0.015) 2.5px)",
            maskImage:
              "linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)",
          }}
        />
      )}
      {/* Subtle top-edge glow */}
      {is3d && (
        <div
          className="absolute top-0 left-0 right-0 h-px z-10 pointer-events-none"
          style={{
            background:
              "linear-gradient(to right, transparent 10%, rgba(118,120,227,0.4) 50%, transparent 90%)",
          }}
        />
      )}
      {card.image ? (
        <img
          src={card.image}
          alt={card.title}
          className="w-full h-full object-cover object-top opacity-80"
        />
      ) : card.code && card.language ? (
        <div className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
            <span className="ml-2 text-white/40 text-[11px] font-mono">
              {card.file}
            </span>
          </div>
          <CodeSnippet code={card.code} language={card.language} />
        </div>
      ) : null}
    </div>
  );
}

export function CodeModeSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Desktop: scroll-driven active index
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");

    const handleScroll = () => {
      const el = scrollAreaRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const scrollable = el.offsetHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const progress = Math.max(0, Math.min(1, -rect.top / scrollable));
      setActiveIndex(
        Math.min(cards.length - 1, Math.floor(progress * cards.length))
      );
    };

    const setup = () => {
      if (mq.matches) {
        window.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();
      } else {
        window.removeEventListener("scroll", handleScroll);
      }
    };

    setup();
    mq.addEventListener("change", setup);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      mq.removeEventListener("change", setup);
    };
  }, []);

  const activeCard = cards[activeIndex]!;

  return (
    <section className="px-6 sm:px-10 border-t border-white/[0.06]">
      <div className="max-w-7xl mx-auto">
        {/* Section header — visible outside scroll area on mobile, inside on desktop */}
        <div className="pt-20 pb-12 lg:hidden">
          <div className="font-ibm-plex-mono mb-3 text-[11px] font-normal uppercase tracking-[0.2em] text-white/40">
            <DecodeText text="A NEW PARADIGM" delay={0} />
          </div>
          <h2 className="text-3xl sm:text-4xl font-normal tracking-[-0.03em] leading-[1.1] max-w-lg">
            Code is the reasoning layer
          </h2>
        </div>

        {/* Desktop: scroll-driven two-column layout */}
        <div
          ref={scrollAreaRef}
          className="hidden lg:block"
          style={{ height: "280vh" }}
        >
          <div className="sticky top-20 pb-20">
            {/* Header stays visible while scrolling through tabs */}
            <div className="pt-12 pb-8">
              <div className="font-ibm-plex-mono mb-3 text-[11px] font-normal uppercase tracking-[0.2em] text-white/40">
                <DecodeText text="A NEW PARADIGM" delay={0} />
              </div>
              <h2 className="text-3xl sm:text-4xl font-normal tracking-[-0.03em] leading-[1.1] max-w-lg">
                Code is the reasoning layer
              </h2>
            </div>
            <div className="flex gap-8">
              {/* Left column — tabs that open as user scrolls */}
              <div className="w-[45%] flex flex-col">
                {cards.map((card, i) => {
                  const isActive = i === activeIndex;
                  return (
                    <div
                      key={card.num}
                      className={`text-left transition-all duration-500 border-l-2 py-4 px-5 ${
                        isActive
                          ? "border-[#7678E3] bg-white/[0.02]"
                          : "border-white/[0.06]"
                      }`}
                    >
                      <h3
                        className={`text-lg font-normal tracking-tight transition-colors duration-500 ${
                          isActive ? "text-white" : "text-white/35"
                        }`}
                      >
                        {card.title}
                      </h3>
                      <div
                        className={`overflow-hidden transition-all duration-500 ease-in-out ${
                          isActive
                            ? "max-h-40 opacity-100 mt-2"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <p className="text-[13px] font-light text-white/50 leading-relaxed">
                          {card.desc}
                        </p>
                        {card.secondDesc && (
                          <p className="text-[13px] font-light text-white/50 leading-relaxed mt-2">
                            {card.secondDesc}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Right column — code block or image, swaps with scroll */}
              <div className="w-[55%] relative">
                {cards.map((card, i) => (
                  <div
                    key={card.num}
                    className={`transition-all duration-500 ease-in-out ${
                      i === activeIndex
                        ? "opacity-100 translate-y-0"
                        : "opacity-0 translate-y-2 pointer-events-none absolute inset-0"
                    }`}
                  >
                    <RightPanel card={card} is3d />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet: click-driven accordion with code inline */}
        <div className="lg:hidden pb-20">
          <div className="flex flex-col">
            {cards.map((card, i) => {
              const isActive = i === activeIndex;
              return (
                <button
                  key={card.num}
                  onClick={() => setActiveIndex(i)}
                  className={`text-left cursor-pointer transition-all duration-300 border-l-2 py-4 px-5 ${
                    isActive
                      ? "border-[#7678E3] bg-white/[0.02]"
                      : "border-white/[0.06] hover:border-white/20 hover:bg-white/[0.01]"
                  }`}
                >
                  <h3
                    className={`text-lg font-normal tracking-tight transition-colors duration-300 ${
                      isActive ? "text-white" : "text-white/35"
                    }`}
                  >
                    {card.title}
                  </h3>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      isActive
                        ? "max-h-[600px] opacity-100 mt-2"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    <p className="text-[13px] font-light text-white/50 leading-relaxed">
                      {card.desc}
                    </p>
                    {card.secondDesc && (
                      <p className="text-[13px] font-light text-white/50 leading-relaxed mt-2">
                        {card.secondDesc}
                      </p>
                    )}
                    <div className="mt-4">
                      <RightPanel card={card} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
